import { DateFormat, formatDate } from "@nova/format"
import { useTranslation } from "@nova/use-translation"
import classNames from "classnames"
import {
    animationClass,
    defaultFolderName,
    defaultProjectName,
} from "features/data-management/workspace/constants"
import { Project, WorkspaceEntityType } from "features/data-management/workspace/types"
import { useEffect, useMemo, useRef, useState } from "react"
import {
    ArrowRight,
    Icon,
    PersonAdd,
    SmallIcon,
    Star,
    StarFill,
    ThreeDotsSettings,
} from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { ContextMenu } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { ClickAwayListener } from "@nova/component-click-away-listener"
import { RenamableText } from "features/data-management/workspace/TODO-put-in-package/RenamableText"
import { ButtonPrimary } from "@nova/component-btn"
import { createContextMenuSections } from "features/data-management/workspace/module"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { useWorkspaceProjectDragAndDrop } from "../../../useWorkspaceProjectDragAndDrop"
import {
    selectActivatingProjectId,
    selectActiveWorkspaceId,
    selectIdRenaming,
    setIdRenaming,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { useLoadingText } from "../../../useLoadingText"
import { setActiveContextMenu } from "features/popups/slice"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { selectFavoritesData } from "features/data-management/workspace/context/selectors"
import {
    toggleFavorite,
    deleteWorkspaceEntity,
    openWorkspaceShareModal,
} from "features/data-management/workspace/context/thunks"
import { updateFire } from "features/entity-caching/fires/thunks"
import { useActivateProject } from "features/data-management/workspace/context/navigation"

export const WorkspaceBodyProjectListItem = ({
    project,
    isInFavorites,
}: {
    project: Project
    isInFavorites: boolean
}) => {
    const key = `list_${project.id}`
    const type = WorkspaceEntityType.Project

    const dispatch = useAppDispatch()
    const { favoriteIds } = useAppSelector(selectFavoritesData)
    const isFavorite = (id: string) => favoriteIds.has(id)
    const activateProject = useActivateProject()

    const { t } = useTranslation()
    const [isHovering, setIsHovering] = useState(false)
    const { isEditor } = useWorkspacePermissions(project.id)
    const activeListProjectContextMenu = useAppSelector(selectActiveContextMenu("ListProject"))
    const contextMenuIsForThisListItem = activeListProjectContextMenu?.data.projectId === project.id
    const renameRef = useRef<HTMLInputElement>(null)
    const idRenaming = useAppSelector(selectIdRenaming())
    const isRenaming = idRenaming === key
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const isActivatingThisProject = useAppSelector(selectActivatingProjectId()) === project.id
    const loadingText = useLoadingText(isActivatingThisProject)

    const contextMenuSections = useMemo(
        () =>
            createContextMenuSections({
                id: project.id,
                key,
                title: project.name || defaultProjectName,
                isFavorite: favoriteIds.has(project.id),
                onToggleFavorite: (id) => dispatch(toggleFavorite(id)),
                onBeginRenaming: isEditor ? (id) => dispatch(setIdRenaming(id)) : undefined,
                onOpenShareModal: (id) => dispatch(openWorkspaceShareModal(id)),
                onOpenInNewTab: (id) => {
                    window.open(`${window.origin}/workspace/${workspaceId}/project/${id}`)
                },
                onDelete: isEditor
                    ? (id, name) => dispatch(deleteWorkspaceEntity({ id, name, type }))
                    : undefined,
            }),
        [project.id, project.name, key, favoriteIds, isEditor, workspaceId, dispatch, type]
    )

    const { dragRef, anythingIsDragging } = useWorkspaceProjectDragAndDrop({
        id: project.id,
        isEditor,
        isRenaming,
        isInFavorites,
    })

    const handleClickAway = () => {
        if (idRenaming) dispatch(setIdRenaming(undefined))
    }

    useEffect(() => {
        if (isRenaming) renameRef.current?.focus()
    }, [isRenaming])

    const handleToggleContextMenu = (force?: boolean) => {
        const shouldHide = (contextMenuIsForThisListItem && !force) || force === false
        if (force === false) setIsHovering(false)
        dispatch(
            setActiveContextMenu(
                shouldHide
                    ? undefined
                    : {
                          type: "ListProject",
                          data: { projectId: project.id, fixedPosition: false },
                          position: { top: 0, left: 0 },
                      }
            )
        )
    }

    const shouldShow3DotsButton =
        (isHovering || contextMenuIsForThisListItem) &&
        contextMenuSections.length > 0 &&
        !isRenaming

    const shouldEmphasizeRow = isHovering || contextMenuIsForThisListItem
    const divRef = useRef<HTMLDivElement>(null)
    dragRef(divRef)

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <div
                ref={divRef}
                className={classNames("h-10 px-2 flex items-center rounded-lg border relative", {
                    "border-transparent": !shouldEmphasizeRow,
                    "border-gray-700": shouldEmphasizeRow,
                    "opacity-20": anythingIsDragging,
                })}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                onDoubleClick={
                    !isRenaming ? () => activateProject(project.id) : (e) => e.stopPropagation()
                }
                onContextMenu={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    dispatch(
                        setActiveContextMenu({
                            type: "ListProject",
                            data: { projectId: project.id, fixedPosition: true },
                            position: { top: e.clientY, left: e.clientX },
                        })
                    )
                }}
            >
                <div className="flex-[4] flex items-center gap-2 truncate">
                    <RenamableText
                        ref={renameRef}
                        Tag="h5"
                        title={
                            isActivatingThisProject
                                ? loadingText
                                : project.name || defaultProjectName
                        }
                        id={project.id}
                        isRenaming={isRenaming}
                        initialTitleIsPlaceholder={
                            (project.name || defaultFolderName) === defaultFolderName
                        }
                        isFullWidth
                        className={classNames(
                            animationClass,
                            "!flex-shrink text-gray-0 !h-6 pr-4",
                            { "hover:text-blue-500": !isRenaming }
                        )}
                        onClick={
                            !isRenaming
                                ? () => activateProject(project.id)
                                : (e) => e.stopPropagation()
                        }
                        onConfirmRenaming={(id, name) => {
                            dispatch(updateFire({ id, updates: { name } }))
                            dispatch(setIdRenaming(undefined))
                        }}
                        onCancelRenaming={() => dispatch(setIdRenaming(undefined))}
                    />
                    {isFavorite(project.id) && !isRenaming && (
                        <SmallIcon Icon={StarFill} className="text-gray-0" />
                    )}
                </div>
                <>
                    <h5 className="flex-[1] pointer-events-none">
                        {t(
                            project.updatedAt
                                ? formatDate({
                                      value: project.updatedAt,
                                      format: DateFormat.MMMDDYYYY,
                                  })
                                : ""
                        )}
                    </h5>
                    <h5 className="flex-[1] pointer-events-none">
                        {t(
                            project.createdAt
                                ? formatDate({
                                      value: project.createdAt,
                                      format: DateFormat.MMMDDYYYY,
                                  })
                                : ""
                        )}
                    </h5>
                </>
                {shouldEmphasizeRow && (
                    <div className="flex gap-4 items-center absolute right-2 top-50% pl-[10rem] bg-gradient-to-l from-gray-800">
                        <ButtonPrimary
                            title="Open"
                            RightIcon={ArrowRight}
                            height="1.5rem"
                            onClick={() => activateProject(project.id)}
                        />
                        <div className="flex gap-1 items-center text-gray-100 relative">
                            <div
                                className="cursor-pointer"
                                onClick={() => dispatch(toggleFavorite(project.id))}
                                onDoubleClick={(e) => e.stopPropagation()}
                            >
                                {!isFavorite(project.id) ? (
                                    <Icon
                                        Icon={Star}
                                        className={classNames(animationClass, "hover:text-gray-0")}
                                    />
                                ) : (
                                    <Icon
                                        Icon={StarFill}
                                        className={classNames(
                                            animationClass,
                                            "text-yellow-500 hover:text-yellow-200"
                                        )}
                                    />
                                )}
                            </div>
                            <Icon
                                Icon={PersonAdd}
                                className={classNames(
                                    animationClass,
                                    "ml-1 hover:text-gray-0 cursor-pointer"
                                )}
                                onClick={() => dispatch(openWorkspaceShareModal(project.id))}
                                onDoubleClick={(e) => e.stopPropagation()}
                            />
                            <Icon
                                Icon={ThreeDotsSettings}
                                className={classNames(
                                    animationClass,
                                    "hover:text-gray-0 cursor-pointer",
                                    { "opacity-0": !shouldShow3DotsButton }
                                )}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleToggleContextMenu()
                                }}
                                onDoubleClick={(e) => e.stopPropagation()}
                            />
                            {activeListProjectContextMenu && (
                                <ContextMenu
                                    sections={contextMenuSections}
                                    onBlur={() => handleToggleContextMenu(false)}
                                    className="flex-none"
                                    isFixedPosition={
                                        activeListProjectContextMenu.data.fixedPosition
                                    }
                                    style={
                                        !activeListProjectContextMenu.data.fixedPosition
                                            ? { top: "1.5rem", right: "0.25rem" }
                                            : undefined
                                    }
                                    top={activeListProjectContextMenu.position.top}
                                    left={activeListProjectContextMenu.position.left}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </ClickAwayListener>
    )
}
